import { useState, useEffect } from 'react';
import middleware from '../../Api/Middleware';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { faXmark, faTrash } from "@fortawesome/free-solid-svg-icons";
import ToastError from '../../Toasts/ToastError';
import { ButtonBlue, ButtonLightGrey } from '../../HouseStyle/Components/Buttons';
import ToastSuccess from '../../Toasts/ToastSuccess';
import { formatCurrency } from '../../../utils';

const PayOrderModalComponent = ({ payRequestModalVisible, setPayRequestModalVisible, order, fetchAll, totalAmount = 0}) => {
    const navigate = useNavigate();
    const [selectedPayments, setSelectedPayments] = useState([]);
    const [remainingAmount, setRemainingAmount] = useState(totalAmount);

    const paymentMethods = [
        {
            payment_method_id: 1,
            method_name: 'Contant',
        },
        {
            payment_method_id: 5,
            method_name: 'Overig',
        }
    ];

    const closeModal = () => {
        setPayRequestModalVisible(false);
        setSelectedPayments([{ 
            payment_method_id: '', 
            amount: '', 
            method_name: '' 
        }]);
    }

    useEffect(() => {
        if (payRequestModalVisible) {
            // fetchPayments();
            setRemainingAmount(totalAmount);
            // Initialize with one empty payment method
            if (selectedPayments.length === 0) {
                setSelectedPayments([{ 
                    payment_method_id: '', 
                    amount: '', 
                    method_name: '' 
                }]);
            }
        }
    }, [payRequestModalVisible, totalAmount]);

    // Calculate remaining amount when selected payments change
    useEffect(() => {
        const totalPaid = selectedPayments.reduce((sum, payment) => {
            return sum + (parseFloat(payment.amount) || 0);
        }, 0);
        setRemainingAmount(totalAmount - totalPaid);
    }, [selectedPayments, totalAmount]);

    const addPaymentMethod = () => {
        setSelectedPayments([
            ...selectedPayments,
            { payment_method_id: '', amount: '', method_name: '' }
        ]);
    };

    const removePaymentMethod = (index) => {
        if (selectedPayments.length > 1) {
            const newPayments = selectedPayments.filter((_, i) => i !== index);
            setSelectedPayments(newPayments);
        }
    };

    const updatePaymentMethod = (index, field, value) => {
        const newPayments = [...selectedPayments];
        newPayments[index][field] = value;
        
        // If updating payment_method_id, also update method_name
        if (field === 'payment_method_id') {
            const selectedMethod = paymentMethods.find(method => method.payment_method_id === Number(value));
            newPayments[index].method_name = selectedMethod ? selectedMethod.method_name : '';
        }
        
        setSelectedPayments(newPayments);
    };

    const handleSubmit = async () => {
        try {
            // Validate that all fields are filled
            const isValid = selectedPayments.every(payment => 
                payment.payment_method_id && payment.amount && parseFloat(payment.amount) > 0
            );
            
            if (!isValid) {
                ToastError('Vul alle velden correct in', 'fill_all_fields', 2000);
                return;
            }

            // Check if total doesn't exceed order amount
            const totalPaid = selectedPayments.reduce((sum, payment) => 
                sum + parseFloat(payment.amount), 0
            );
            
            if (totalPaid > totalAmount) {
                ToastError('Het totale bedrag mag niet hoger zijn dan het orderbedrag');
                return;
            }

            // Submit payment data
            const paymentData = {
                sales_channel_id: order.sales_channel_id,
                payments: selectedPayments.map(payment => (
                    {
                        order_id: order.order_id,
                        value: Number(payment.amount),
                        payment_method_id: payment.payment_method_id
                    }
                )),
            };

            await middleware.post('saleschannels/checkout', paymentData);
            
            ToastSuccess('Betaling succesvol verwerkt');
            fetchAll();
            closeModal();
        } catch (error) {
            console.error('Error processing payment:', error);
            ToastError('Er ging iets fout bij het verwerken van de betaling');
        }
    };

    return (
        <Modal
            id="modalPayRequest"
            onHide={() => closeModal()}
            show={payRequestModalVisible}
            className="colormodals"
            size="lg"
            centered
        >
            <Modal.Header className="modalTop justify-content-between">
                <h4>Bestelling betalen</h4>
                <button 
                    variant="transparent" 
                    className='btn' 
                    onClick={() => closeModal()}
                >
                    <FontAwesomeIcon icon={faXmark} className="closeModal" />
                </button>
            </Modal.Header>
            
            <Modal.Body className="modalCenter d-flex flex-column">
                <div className="mb-3">
                    <h6>Totaal orderbedrag: {formatCurrency(totalAmount)}</h6>
                    <h6 className={remainingAmount < 0 ? 'text-danger' : remainingAmount > 0 ? 'text-warning' : 'text-success'}>
                        Resterend bedrag: {formatCurrency(remainingAmount)}
                    </h6>
                </div>
                
                {selectedPayments.map((payment, index) => (
                    <div key={index} className="row mb-4 align-items-end">
                        <div className="col-md-5">
                            <label className="form-label">Betaalmethode</label>
                            <select
                                className="form-select standardInput"
                                value={payment.payment_method_id}
                                onChange={(e) => updatePaymentMethod(index, 'payment_method_id', e.target.value)}
                            >
                                <option hidden value="">Selecteer methode</option>
                                {paymentMethods.map(method => (
                                    <option key={method.payment_method_id} value={method.payment_method_id}>
                                        {method.method_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="col-md-4">
                            <label className="form-label">Bedrag (€)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max={totalAmount}
                                className="form-control standardInput"
                                placeholder="0.00"
                                value={payment.amount}
                                onChange={(e) => updatePaymentMethod(index, 'amount', e.target.value)}
                            />
                        </div>
                        
                        <div className="col-md-2">
                            <ButtonLightGrey
                                text={'Rest'}
                                onClickFunction={() => updatePaymentMethod(index, 'amount',  (Number(payment.amount) + remainingAmount).toFixed(2))}
                                disabled={remainingAmount <= 0}
                            />
                        </div>
                        
                        <div className="col-md-1">
                            {selectedPayments.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => removePaymentMethod(index)}
                                    title="Verwijder betaalmethode"
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </div>
                    </div>
                ))}

                <div className="mb-3">
                    <ButtonBlue
                        text={'Betaalmethode toevoegen'}
                        onClickFunction={addPaymentMethod}
                    />
                </div>

                {/* Summary */}
                <div className="card">
                    <div className="card-body">
                        <h6>Overzicht</h6>
                        {selectedPayments.map((payment, index) => 
                            payment.method_name && payment.amount && (
                                <div key={index} className="d-flex justify-content-between">
                                    <span>{payment.method_name}:</span>
                                    <span>{formatCurrency(parseFloat(payment.amount) || 0)}</span>
                                </div>
                            )
                        )}
                        <hr />
                        <div className="d-flex justify-content-between fw-bold">
                            <span>Totaal betaald:</span>
                            <span>{formatCurrency(selectedPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                            <span>Resterend:</span>
                            <span className={remainingAmount < 0 ? 'text-danger' : remainingAmount > 0 ? 'text-warning' : 'text-success'}>
                                {formatCurrency(remainingAmount)}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer className="modalBottom">
                <ButtonBlue
                    text={'Betaling verwerken'}
                    onClickFunction={handleSubmit}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default PayOrderModalComponent;
